import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { useAuth } from "@/auth/AuthProvider";
import { LINKEDIN_HREF, ROUTES, SUPPORT_EMAIL } from "./links";
import "./site.css";
import "./site-extra.css";
import "./site-mobile.css";

const DARK_BG = "#05080F";

/**
 * Reveal-on-scroll (the prototype's `siteReveal`): `.rv` elements fade in once they come into
 * view. Re-run whenever the page changes. With reduced motion the CSS shows them immediately.
 */
function useReveal(root: React.RefObject<HTMLElement | null>, key: string) {
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const pending = () => [...el.querySelectorAll<HTMLElement>(".spage .rv:not(.in)")];
    const showVisible = (ratio: number) =>
      pending().forEach((r) => {
        if (r.getBoundingClientRect().top < window.innerHeight * ratio) r.classList.add("in");
      });
    let io: IntersectionObserver | null = null;
    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver(
        (entries) =>
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add("in");
              io?.unobserve(e.target);
            }
          }),
        { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
      );
      pending().forEach((r) => io!.observe(r));
    }
    const raf = requestAnimationFrame(() => showVisible(0.9));
    const late = setTimeout(() => showVisible(1), 500);
    const onScroll = () => showVisible(0.88);
    window.addEventListener("scroll", onScroll, { passive: true });
    // Pages that render late (the blog, after its query) add .rv nodes after this effect.
    const mo = new MutationObserver(() => pending().forEach((r) => io?.observe(r)));
    mo.observe(el, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(late);
      window.removeEventListener("scroll", onScroll);
      io?.disconnect();
      mo.disconnect();
    };
  }, [root, key]);
}

const NAV = [
  { to: ROUTES.employers, label: "For employers" },
  { to: ROUTES.how, label: "How it works" },
  { to: ROUTES.team, label: "Our team" },
];

/** The marketing site's frame: the dark `#site` root, the nav, the footer, the phone CTA. */
export function SiteLayout() {
  const { pathname, hash } = useLocation();
  const root = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user } = useAuth();
  const onHome = pathname === "/";

  // The prototype's siteBg(true): the page behind the site is dark, so overscroll stays dark.
  useEffect(() => {
    const html = document.documentElement;
    const prev = [html.style.background, document.body.style.background];
    html.style.background = DARK_BG;
    document.body.style.background = DARK_BG;
    return () => {
      html.style.background = prev[0];
      document.body.style.background = prev[1];
    };
  }, []);

  // sgo(): every page starts at the top, menu closed.
  useLayoutEffect(() => {
    setMenuOpen(false);
    if (hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView();
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [pathname, hash]);

  useReveal(root, pathname);

  return (
    <div id="site" ref={root} className={onHome ? "darkhome" : undefined}>
      <nav className="snav" aria-label="Main">
        <div className="snav-in">
          <Link className="slogo" to={ROUTES.home} aria-label="WealthyPlanet, back to home">
            <span className="wplock rev" />
          </Link>
          <div className={`links${menuOpen ? " open" : ""}`} id="slinks">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className={({ isActive }) => (isActive ? "on" : "")}>
                {n.label}
              </NavLink>
            ))}
          </div>
          <div className="r">
            <button
              type="button"
              className="burger"
              aria-label="Menu"
              aria-expanded={menuOpen}
              aria-controls="slinks"
              onClick={() => setMenuOpen((o) => !o)}
            >
              &#9776;
            </button>
            {user ? (
              <Link className="sbtn" to={ROUTES.app} data-testid="site-open-app">
                Open App
              </Link>
            ) : (
              <Link className="sbtn" to={ROUTES.signin} data-testid="site-signin">
                Sign in
              </Link>
            )}
            <Link className="sbtn p" to={ROUTES.start} data-testid="site-start">
              Start<span className="lg"> your plan</span>
            </Link>
          </div>
        </div>
      </nav>

      <main>
        <Outlet />
      </main>

      <SiteFooter />

      <div className="mcta">
        <Link className="sbtn p" to={ROUTES.start}>
          Start your plan
        </Link>
        <span>$9.99 / mo</span>
      </div>
    </div>
  );
}

export function SiteFooter() {
  return (
    <footer className="sfoot dawn dawn-l3">
      <div className="wrap">
        <div className="sfoot-in">
          <div>
            <div
              className="slogo"
              style={{ cursor: "default" }}
              role="img"
              aria-label="WealthyPlanet"
            >
              <span className="wplock" />
            </div>
            <p className="blurb">
              A financial plan built from your own accounts, rebuilt whenever something changes.
              Burlington, Ontario.
            </p>
          </div>
          <div>
            <h4>Product</h4>
            <ul>
              <li>
                <Link to={ROUTES.how}>How it works</Link>
              </li>
              <li>
                <Link to={ROUTES.abi}>What Abi does</Link>
              </li>
              <li>
                <Link to={ROUTES.pricing}>Pricing</Link>
              </li>
              <li>
                <Link to={ROUTES.security}>Security</Link>
              </li>
            </ul>
          </div>
          <div>
            <h4>Company</h4>
            <ul>
              <li>
                <Link to={ROUTES.team}>About</Link>
              </li>
              <li>
                <Link to={ROUTES.employers}>For employers</Link>
              </li>
              <li>
                <Link to={ROUTES.blog}>Blog</Link>
              </li>
              <li>
                <Link to={ROUTES.contact}>Contact</Link>
              </li>
              <li>
                <a href={LINKEDIN_HREF} target="_blank" rel="noopener noreferrer">
                  LinkedIn
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4>Legal</h4>
            <ul>
              <li>
                <Link to={ROUTES.privacy}>Privacy policy</Link>
              </li>
              <li>
                <Link to={ROUTES.terms}>Terms of service</Link>
              </li>
              <li>
                <a href={`mailto:${SUPPORT_EMAIL}`}>Support</a>
              </li>
            </ul>
          </div>
        </div>
        <div className="legal">
          <span>&copy; {new Date().getFullYear()} WealthyPlanet. All rights reserved.</span>
          <span className="sp">
            Abi produces projections, not guarantees. Not a licensed dealer.
          </span>
        </div>
      </div>
    </footer>
  );
}
